import React, { useEffect, useState } from "react";
import moment from 'moment';
import QuizPartner from "./QuizPartner";
import "./Quiz.scss";
import { useEventContext } from "../../context/EventContext";
import { eventPollAnswerClick, eventPollOpenAnswers, eventPollRatingAnswers } from "../../../../api/player";
import { useLocaleContext } from "../../context/LocaleContext";
import UIButton from "../../../../globalComponents/UIComponents/UIButton";
import UIInput from "../Authentication/InputField/UIInput";
import { PollRatingAnswer } from "../Polls/Rating";
import CCountDown from "../UI/CCountDown";
import { useQuizContext } from "../../context/QuizContext";
import { usePollsContext } from "../../context/PollsContext";
import { CButton } from "../UI";
import { includesAny } from "../../helpers/array";

const ALREADY_PASSED_ERROR_STATUS = 409;

const ANSWER_TYPE_SKIP = 'skip';
const POLL_TYPE = {
    STANDART: 'standard',
    OPEN: 'open',
    RATING: 'rating',
};

const PollProgressCounter = ({ current, total }) => {
    const { get: getLocale } = useLocaleContext();

    const progressPercent = Math.round((current * 100) / total);
    const width = `${progressPercent}%`;

    return (
        <div className="quiz-question-progress-wrapper">
            <div className="quiz-question-progress">
                <span className="quiz-question-progress-bar" style={{ width }}></span>

                <span className="quiz-question-progress-value">{`${current} ${getLocale("quiz.progressOf")} ${total}`}</span>
                <span className="quiz-question-progress-percent">{`${progressPercent} %`}</span>
            </div>
        </div>
    );
};

const PollAnswerButton = ({
    answer,
    onClick,
    isResult = false,
    isCorrect = false,
    isSelected = false,
    isMultiple = false,
}) => {
    const { getProperty } = useLocaleContext();
    let className = "quiz-question-answer";

    if (!isResult && onClick) {
        className += " quiz-question-answer-active";
    }

    if (isResult && isSelected && isCorrect) {
        className += " quiz-question-answer-correct";
    } else if (isResult && isSelected) {
        className += " quiz-question-answer-wrong";
    } else if (isResult && isCorrect) {
        className += " quiz-question-answer-miss";
    } else if (isSelected) {
        className += " quiz-question-answer-selected";
    }

    return (
        <div>
            <div className={className} onClick={isResult ? null : onClick}>
                <div className="quiz-question-answer-text">{getProperty(answer, "text")}</div>
            </div>
        </div>
    );
};

const Poll = ({ quiz, preview, poll, pollId, onClickNext, hasNextPoll, onAnswer, result, fade }) => {
    const { get: getLocale, getProperty } = useLocaleContext();
    const { onQuizUpdate } = useQuizContext();
    const { answeredPollIds } = usePollsContext();
    const [selectedAnswers, setSelectedAnswers] = useState([]);
    const [ratingAnswers, setRatingAnswers] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        setSelectedAnswers([]);
        setRatingAnswers({});
    }, [pollId]);

    const isComplete = poll?.type === POLL_TYPE.RATING
        ? (Array.isArray(poll?.answer) && poll.answer.filter(({ type }) => type !== ANSWER_TYPE_SKIP)?.every(({ id }) => ratingAnswers[id] > 0)) ||
            (Array.isArray(poll?.answer)
            && !!poll.answer.find((answer) => answer.type === ANSWER_TYPE_SKIP && selectedAnswers.find(({id}) => id === answer.id)))
        : (!poll?.multiple && selectedAnswers.length) ||
            (poll?.multiple &&
            selectedAnswers.length >= poll?.answerCountMin &&
            selectedAnswers.length <= poll?.answerCountMax);

    const resultAnswers = (poll?.type === POLL_TYPE.RATING && !selectedAnswers.length) ? ratingAnswers : selectedAnswers;

    const nextQuestionButtonText = hasNextPoll
        ? getLocale("quiz.nextPoll.button")
        // : getLocale("quiz.finishPoll.button"); // TODO: вернуть назад в будущем, костыль для COMDI-2426
        : getLocale("quiz.answer.button");

    useEffect(() => {
        if (selectedAnswers.length > 0 && selectedAnswers.some(({ type }) => type === ANSWER_TYPE_SKIP)) {
            onAnswer(selectedAnswers, poll);
        }
    }, [selectedAnswers]);

    const handleOpenAnswers = (index, value) => {
        const _selectedAnswers = [...selectedAnswers];
        _selectedAnswers[index] = value;
        setSelectedAnswers(_selectedAnswers);
    };

    const handleRatingAnswer = (answer, value) => {
        if (!!selectedAnswers.length) {
            setSelectedAnswers([]);
        }

        setRatingAnswers(prev => ({ ...prev, [answer.id]: value }));
    };

    const handleStandardAnswer = (answer) => {
        if (!!Object.keys(ratingAnswers)?.length) {
            setRatingAnswers({});
        }

        let _selectedAnswers = [...selectedAnswers];
        const index = _selectedAnswers.findIndex(({ id }) => id === answer.id);
        if (-1 === index) {
            if (!poll.multiple) {
                _selectedAnswers = [];
            } else if (poll.answerCountMax <= _selectedAnswers.length) {
                return;
                // answers.shift();
            }
            _selectedAnswers.push(answer);
        } else {
            _selectedAnswers.splice(index, 1);
        }
        setSelectedAnswers(_selectedAnswers);
    };

    const handleSkipQuestion = () => {
        const answer = poll.answer?.find(({ type }) => type === ANSWER_TYPE_SKIP);
        handleStandardAnswer(answer);
    };

    const handlePollAnswer = async () => {
        if (preview) return;

        setIsSubmitting(true);

        const answers = Array.isArray(resultAnswers)
            ? resultAnswers.map(({ id }) => id)
            : Object.keys(resultAnswers)?.map(({ id }) => id);

        try {
            if (!includesAny(answeredPollIds, answers) && !fade) {
                await onAnswer(resultAnswers, poll);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const answerInputs = {
        standard: () => poll.answer?.map((answer) => (
            answer.type !== ANSWER_TYPE_SKIP && <PollAnswerButton
                key={answer.id}
                answer={answer}
                isMultiple={poll?.multiple}
                isSelected={selectedAnswers.find(({ id }) => id === answer.id)}
                isResult={!!result}
                isCorrect={result ? answer.correct : false}
                onClick={() => handleStandardAnswer(answer)}
            />
        )),
        open: () => <>
            <UIInput
                name={'selectedAnswers[]'}
                type={'text'}
                maxLength={600}
                placeholder={getLocale('polls.enterAnswer')}
                value={selectedAnswers[0]?.type === ANSWER_TYPE_SKIP ? '' : (selectedAnswers[0] || '')}
                onChange={(value) => handleOpenAnswers(0, value)}
            />
        </>,
        rating: () => poll.answer?.map((answer) => (
            answer.type !== ANSWER_TYPE_SKIP && <PollRatingAnswer
                key={answer.id}
                answer={answer}
                isResult={!!result}
                value={ratingAnswers[answer.id]}
                onChange={(value) => handleRatingAnswer(answer, value)}
            />
        ))
    }

    if (!poll) {
        return null;
    }

    const willRenderCountDown = !!(!preview && quiz.modalStartAt && quiz.modalTimeout && moment.unix(quiz.modalStartAt + quiz.modalTimeout).isAfter(moment()));

    return (
        <div className={`quiz-poll poll-${poll?.type}` + (fade ? " quiz-poll-fade" : "")}>
            <div className="quiz-question-text">{getProperty(poll, "text")}</div>
            {poll.imageUrl && (
                <div className="quiz-question-image-container">
                    <img
                        src={poll.imageUrl}
                        className="quiz-question-image"
                        alt={getProperty(poll, "text")}
                        title={getProperty(poll, "text")}
                    />
                </div>
            )}
            <div className="quiz-question-answers">
                {answerInputs[poll.type]()}
            </div>
            {true === result?.isCorrect && (
                <div className="quiz-question-resultMessage quiz-question-resultMessage-correct">
                    <div className="quiz-question-resultMessage-title">{getLocale("quiz.poll.correctMessage")}</div>
                    {/*<div>Объясняем почему правильный ответ</div>*/}
                </div>
            )}
            {false === result?.isCorrect && (
                <div className="quiz-question-resultMessage quiz-question-resultMessage-wrong">
                    <div className="quiz-question-resultMessage-title">{getLocale("quiz.poll.incorrectMessage")}</div>
                    {/*<div>Объясняем почему неправильный ответ</div>*/}
                </div>
            )}
            <div className="quiz-question-footer">
                {result ? (
                    <UIButton color="primary" onClick={onClickNext}>
                        {nextQuestionButtonText}
                    </UIButton>
                ) : (
                    <>
                        <UIButton
                            color="primary"
                            onClick={handlePollAnswer}
                            disabled={!isComplete || isSubmitting}
                        >
                            {getLocale("quiz.answer.button")}
                        </UIButton>
                        {!!poll.answer?.find(({ type }) => type === ANSWER_TYPE_SKIP) && (
                            <CButton
                                size="lg"
                                label={getLocale("quiz.answer.skip")}
                                onClick={handleSkipQuestion}
                            />
                        )}
                    </>
                )}
                {willRenderCountDown && <CCountDown start={quiz.modalStartAt} timeout={quiz.modalTimeout} onTimeOut={() => onQuizUpdate(quiz)} />}
            </div>
            <div>{(poll.multiple && poll.answerCountMax > 1) && <>
                {poll.answerCountMin > 1 && <p>{getLocale('polls.select.several-only')}</p>}
                {poll.answerCountMin === 1 && <p>{getLocale('polls.select.several')}</p>}
            </>}</div>
        </div>
    );
};

export default ({ quiz, preview, previewPollIndex, authorizationCheck, isShowHeader = true }) => {
    const fadeTimeout = 500;
    const { event } = useEventContext();
    const { get: getLocale, getProperty } = useLocaleContext();
    const { answeredPollIds, setAnsweredPollIds } = usePollsContext();
    const [pollIndex, setPollIndex] = useState(quiz.polls.findIndex(({ id }) => -1 === answeredPollIds.indexOf(id)) || 0);
    const resultPollIndex = previewPollIndex ?? pollIndex;
    const [poll, setPoll] = useState(quiz.polls[resultPollIndex]);
    const [pollsResults, setPollsResults] = useState({});
    const [fade, setFade] = useState(false);
    const [finalScreen, setFinalScreen] = useState(resultPollIndex === 'finalScreen');

    useEffect(() => {
        setPoll(quiz.polls[resultPollIndex] || null);
    }, [resultPollIndex]);

    const handlePollAnswer = async (answers, poll) => {
        const isAuth = await authorizationCheck();

        if (!isAuth) {
            return;
        }

        try {
            let answersId = poll.type === POLL_TYPE.RATING ? Object.keys(answers) : answers.map(({ id }) => id);

            if (poll.type === POLL_TYPE.STANDART
                || Array.isArray(answers) && answers.find(({type}) => type === ANSWER_TYPE_SKIP)
            ) {
                await eventPollAnswerClick(event.eventKey, poll.id, answers.map(({ id }) => id), true);
            } else {
                poll.type === POLL_TYPE.OPEN
                    ? await eventPollOpenAnswers(event.eventKey, { pollId: poll.id, answers })
                    : await eventPollRatingAnswers(event.eventKey, poll.id, { answers });
            }

            setAnsweredPollIds([...answeredPollIds, poll.id])

            if (!quiz.correctAnswerVisible || poll.type !== POLL_TYPE.STANDART) {
                handleNextPoll();
            } else {
                const expectedAnswersId = poll.answer.filter(({ correct }) => correct).map(({ id }) => id);
                const isCorrect = !expectedAnswersId.length || (expectedAnswersId.length === answersId.length && expectedAnswersId.every(id => answersId.includes(id)));
                setPollsResults({ ...pollsResults, [poll.id]: { isCorrect, answers: answersId } });
            }
        } catch (error) {
            console.error(error);

            if (error.status === ALREADY_PASSED_ERROR_STATUS) {
                setPoll(null);
                setFinalScreen(false);

                const pollsIds = [...answeredPollIds, ...quiz.polls.map(({id}) => id)];
                setAnsweredPollIds([...new Set(pollsIds)]);
            }
        }
    };

    const handleNextPoll = async () => {
        setFade(true);
        await new Promise((r) => setTimeout(r, fadeTimeout));
        const nextPollIndex = resultPollIndex + 1;
        const nextPoll = quiz.polls[nextPollIndex];
        setFinalScreen(!nextPoll);
        setPollIndex(nextPollIndex);
        setFade(false);
    };

    const finalScreenTitle = getProperty(quiz.finalScreen, "title");
    const finalScreenSubitle = getProperty(quiz.finalScreen, "subtitle");
    const finalScreenButton = getProperty(quiz.finalScreen, "buttonText");

    return (
        <div className="quiz">
            {isShowHeader && (
                <div className="quiz-header">
                    {quiz.partners?.length > 0 && (
                        <div className="quiz-partners">
                            {quiz.partners.map((partner) => (
                                <QuizPartner key={partner.id} partner={partner} />
                            ))}
                        </div>
                    )}
                    <div className="quiz-title">{getProperty(quiz, "title")}</div>
                </div>
            )}
            {poll ? <>
                <div className="quiz-description">{getProperty(quiz, "description")}</div>
                <div>
                    {quiz.polls.length > 1 && (
                        <PollProgressCounter current={resultPollIndex} total={quiz.polls.length} />
                    )}
                    <Poll
                        quiz={quiz}
                        preview={preview}
                        pollId={poll.id}
                        poll={poll}
                        onClickNext={handleNextPoll}
                        hasNextPoll={!!quiz.polls[resultPollIndex + 1]}
                        onAnswer={handlePollAnswer}
                        result={pollsResults[poll.id]}
                        fade={fade}
                    />
                </div>
            </> : finalScreen ? <>
                <div>
                    {finalScreenTitle && <div className="quiz-final-screen-title">{finalScreenTitle}</div>}
                    {finalScreenSubitle && <div className="quiz-final-screen-subtitle">{finalScreenSubitle}</div>}
                    {quiz.finalScreen.pictureUrl && <img className="quiz-final-screen-image" alt="" src={quiz.finalScreen.pictureUrl} />}
                </div>
                {!!quiz.finalScreen.buttonUrl
                    && <a href={quiz.finalScreen.buttonUrl} target="_blank">
                        <UIButton color="primary" fullWidth>
                            {finalScreenButton}
                        </UIButton>
                    </a>
                }
            </> : <></>
            }
        </div>
    );
};
